import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "../../../../_context/UserContext";

interface SuccessModalProps {
  show: boolean;
  onHide: () => void;
  successMessage: string;
  isReportSaved?: boolean;
  onSendEmails?: () => void;
  emailsSent?: boolean;
  onEmailsSent?: () => void;
  clientEmailSent?: boolean;
  cleanerEmailSent?: boolean;
  isExitFlow?: boolean;
}

const SubmitReportSuccess: React.FC<SuccessModalProps> = ({ 
  show, 
  onHide, 
  successMessage,
  isReportSaved = false,
  onSendEmails,
  emailsSent = false,
  onEmailsSent,
  clientEmailSent = false,
  cleanerEmailSent = false,
  isExitFlow = false
}) => {
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const navigate = useNavigate();
  const { isUserAdmin } = useUserContext();

  // Handle different scenarios when modal is shown
  useEffect(() => {
    if (show) {
      if (isExitFlow) {
        // Exit flow - check if report is saved
        if (!isReportSaved) {
          setShowSavePrompt(true);
        } else {
          // Report is saved, show simple exit confirmation
          setShowExitConfirmation(true);
        }
      } else {
        // Save success flow
        setShowSaveSuccess(true);
      }
    }
  }, [show, isExitFlow, isReportSaved]);

  const handleClose = () => {
    if (isExitFlow) {
      // Exit flow - check if report is saved
      if (!isReportSaved) {
        setShowSavePrompt(true);
      } else {
        // Report is saved, show simple exit confirmation
        setShowExitConfirmation(true);
      }
    } else {
      // Save success flow
      setShowSaveSuccess(true);
    }
  };

  const handleConfirmExit = () => {
    setShowExitConfirmation(false);
    setShowSavePrompt(false);
    setShowSaveSuccess(false);
    onHide();
    const targetPath = isUserAdmin ? '/admin' : '/reports';
    navigate(targetPath);
  };

  const handleCancelExit = () => {
    setShowExitConfirmation(false);
    setShowSavePrompt(false);
    setShowSaveSuccess(false);
    onHide();
  };

  const handleSaveReport = () => {
    setShowSavePrompt(false);
    // Trigger save report action
    if (onSendEmails) {
      onSendEmails();
    }
    // Show exit confirmation after save
    setShowExitConfirmation(true);
  };

  const handleSkipSave = () => {
    setShowSavePrompt(false);
    // Exit without saving
    handleConfirmExit();
  };

  return (
    <>
      {/* Save Success Modal */}
      <Modal show={showSaveSuccess} onHide={handleCancelExit}>
        <Modal.Header closeButton>
          <Modal.Title>Success</Modal.Title>
        </Modal.Header>
        <Modal.Body>Report saved successfully!</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelExit}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Save Prompt Modal */}
      <Modal show={showSavePrompt} onHide={handleCancelExit}>
        <Modal.Header closeButton>
          <Modal.Title>Exit Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Do you want to exit? Make sure you save the report before you exit.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelExit}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleSkipSave}>
            Exit
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Simple Exit Confirmation Modal */}
      <Modal show={showExitConfirmation} onHide={handleCancelExit}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Exit</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to exit?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelExit}>
            Stay Here
          </Button>
          <Button variant="primary" onClick={handleConfirmExit}>
            Exit
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SubmitReportSuccess;
